import clsx from 'clsx';
import _ from 'lodash';
import history from '@history';
import { Chip, Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { CardNumberColored } from '../CardNumberColored/CardNumberColored';

const generateContainerStyle = (cols, entryStyle) => {
  const defaultStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols || 4}, minmax(0, 1fr))`,
    gap: '1rem',
    marginTop: '1rem',
    gridAutoFlow: 'row',
  };
  return _.merge({}, defaultStyle, entryStyle);
};

/**
 * CardWidgets
 *
 * Componente para mostrar un conjunto de widgets en tarjetas, con opción de encabezado y enlace.
 *
 * @component
 * @param {string} [className] - Clase CSS adicional para el contenedor
 * @param {string} [label] - Título o etiqueta principal de la tarjeta
 * @param {{ label: string, color?: string, variant?: string, path?: string }} [link] - Objeto de enlace para mostrar un chip de acción
 * @param style
 * @param girdContainerStyle - Estilos de contenedor de grid
 * @param gridItemStyle - Estilos de elementos de grid
 * @param {number} [cols=4] - Número de columnas para el grid de widgets
 * @param {Array<object>} [data=[]] - Datos de los widgets a mostrar (cada objeto se pasa a CardNumberColored)
 * @param props
 * @returns {JSX.Element}
 */
export const CardWidgets = ({
  className,
  label,
  link,
  style,
  girdContainerStyle,
  gridItemStyle,
  cols = 4,
  data = [],
  ...props
}) => {
  const handleRedirect = (path) => {
    if (path === undefined) return;
    history.push(path);
  };

  return (
    <Paper
      className={clsx(
        'flex flex-col px-24 pb-24 pt-14 shadow rounded-2xl overflow-y-scroll min-w-[100px] h-auto',
        className
      )}
      sx={style}
      {...props}
    >
      <div className="flex w-full flex-row items-center justify-between">
        {label !== undefined && (
          <Typography className="text-lg font-medium tracking-tight leading-6 truncate">
            {label}
          </Typography>
        )}
        {link !== undefined && (
          <Chip
            size="small"
            color={link?.color}
            variant={link.variant || 'filled'}
            disabled={link.path === undefined}
            onClick={() => handleRedirect(link?.path)}
            label={link.label || 'Ver más'}
          />
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-1 grid-flow-row gap-24 w-full mt-8 sm:mt-16">
        <div className="flex flex-col">
          <Box sx={generateContainerStyle(cols, girdContainerStyle)}>
            {data.map((item, indexItem) => (
              <Box
                sx={item?.style}
                className={clsx(item.className)}
                key={`item_k_dash_${indexItem}`}
              >
                <CardNumberColored
                  label={item?.label}
                  value={item.value}
                  variant={item?.variant}
                  {..._.omit(item, ['className', 'style'])}
                />
              </Box>
            ))}
          </Box>
        </div>
      </div>
    </Paper>
  );
};
